import { useEffect, useRef, type ChangeEvent } from 'react'
import { Button } from '@/common/ui/Form/Button/Button'
import { useOptionalToast } from '@/common/ui/Toast/toastContext'
import { useCompanyStatsDetailedBrandComparison } from '../../model/useCompanyStatsDetailedBrandComparison'
import {
    getTopBrandsByVolume,
    type DetailedComparisonDetailView,
    type DetailedComparisonFormat,
} from '../../lib/detailedBrandComparison'
import { CompanyStatsDetailedBrandComparisonHeader } from './CompanyStatsDetailedBrandComparisonHeader'
import { CompanyStatsDetailedBrandComparisonBrandSelector } from './CompanyStatsDetailedBrandComparisonBrandSelector'
import { CompanyStatsDetailedBrandComparisonBlocksTable } from './CompanyStatsDetailedBrandComparisonBlocksTable'
import { CompanyStatsDetailedBrandComparisonCriteriaTables } from './CompanyStatsDetailedBrandComparisonCriteriaTables'
import type { CompanyStatsDataFetchers } from '../../model/hooks'
import type { CompanyStatsComparisonMode } from '../../model/useCompanyStatsBrandComparison'

type CompanyStatsDetailedBrandComparisonDashboardProps = {
    orgId: string
    compareBy: CompanyStatsComparisonMode
    allEntities: Array<{ id: string; name: string }>
    selectedEntityIds: string[]
    format: DetailedComparisonFormat
    detailView: DetailedComparisonDetailView
    onCompareByChange: (e: ChangeEvent<HTMLSelectElement>) => void
    onSelectionChange: (ids: string[]) => void
    onFormatChange: (format: DetailedComparisonFormat) => void
    onDetailViewChange: (view: DetailedComparisonDetailView) => void
    fetchers?: CompanyStatsDataFetchers
}

export const CompanyStatsDetailedBrandComparisonDashboard = ({
    orgId,
    compareBy,
    allEntities,
    selectedEntityIds,
    format,
    detailView,
    onCompareByChange,
    onSelectionChange,
    onFormatChange,
    onDetailViewChange,
    fetchers,
}: CompanyStatsDetailedBrandComparisonDashboardProps) => {
    const toast = useOptionalToast()
    const lastErrorMessageRef = useRef<string | null>(null)
    const { brandOptions, rows, isCommonLoading, isScoresLoading, error, refetch } =
        useCompanyStatsDetailedBrandComparison(orgId, compareBy, allEntities, selectedEntityIds, fetchers)

    const hasAppliedInitialSelection = useRef(false)

    useEffect(() => {
        hasAppliedInitialSelection.current = false
    }, [compareBy])

    useEffect(() => {
        if (!error) {
            lastErrorMessageRef.current = null
            return
        }

        if (lastErrorMessageRef.current === error.message) {
            return
        }

        lastErrorMessageRef.current = error.message
        toast?.showToast({
            type: 'error',
            message: `Ошибка загрузки: ${error.message}`,
        })
    }, [error, toast])

    useEffect(() => {
        if (hasAppliedInitialSelection.current) return
        if (selectedEntityIds.length > 0) {
            hasAppliedInitialSelection.current = true
            return
        }
        if (isCommonLoading) return

        const hasCounts = brandOptions.some(brand => brand.productCount > 0)
        if (!hasCounts) return

        hasAppliedInitialSelection.current = true
        onSelectionChange(getTopBrandsByVolume(brandOptions, 2))
    }, [brandOptions, isCommonLoading, onSelectionChange, selectedEntityIds.length])

    if (error) {
        return (
            <div className='flex flex-col gap-6'>
                <CompanyStatsDetailedBrandComparisonHeader
                    compareBy={compareBy}
                    format={format}
                    detailView={detailView}
                    onCompareByChange={onCompareByChange}
                    onFormatChange={onFormatChange}
                    onDetailViewChange={onDetailViewChange}
                />
                <Button type='button' emphasis='accent' onClick={refetch}>
                    Обновить страницу
                </Button>
            </div>
        )
    }

    return (
        <div className='flex flex-col gap-6'>
            <CompanyStatsDetailedBrandComparisonHeader
                compareBy={compareBy}
                format={format}
                detailView={detailView}
                onCompareByChange={onCompareByChange}
                onFormatChange={onFormatChange}
                onDetailViewChange={onDetailViewChange}
            />

            <CompanyStatsDetailedBrandComparisonBrandSelector
                compareBy={compareBy}
                brandOptions={brandOptions}
                selectedBrandIds={selectedEntityIds}
                onSelectionChange={onSelectionChange}
                isLoading={isCommonLoading}
            />

            {detailView === 'blocks' ? (
                <CompanyStatsDetailedBrandComparisonBlocksTable
                    compareBy={compareBy}
                    rows={rows}
                    format={format}
                    isLoading={isScoresLoading}
                />
            ) : (
                <CompanyStatsDetailedBrandComparisonCriteriaTables
                    compareBy={compareBy}
                    rows={rows}
                    format={format}
                    isLoading={isScoresLoading}
                />
            )}
        </div>
    )
}
